import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBars,
  FaCheck,
  FaDiscord,
  FaExternalLinkAlt,
  FaTimes,
} from 'react-icons/fa';
import nexusHero from '../../img/studio/nexus-hero.png';
import nexusLogo from '../../img/studio/nexus-logo.png';
import avatarVihs from '../../img/AvatarVihs.svg';
import { studioService } from '../../services/apiService';
import VihsMark from '../../components/VihsMark';
import { useProjectRequest } from '../../contexts/ProjectRequestContext';
import './Studio.css';

const CONTACT_URL = 'https://discord.gg/ScdKWjtkVd';
const INSTAGRAM_URL = 'https://instagram.com/vihsoficial';

const projects = [
  {
    index: '01',
    name: 'Nexus SMP',
    client: 'Nexus',
    category: 'Ecossistema Minecraft',
    description:
      'Sistema completo para o servidor Nexus: site oficial, launcher, autenticação Discord, planos VIP com pagamentos, economia web, chat de suporte e painel administrativo integrado à operação da comunidade.',
    tags: ['Website', 'Sistema', 'Discord', 'Launcher', 'Pagamentos'],
    theme: 'nexus',
    label: 'Case entregue',
    href: 'https://nexussmp.com.br/',
    domain: 'nexussmp.com.br',
    image: nexusHero,
    logo: nexusLogo,
    deliverables: [
      'Site e identidade digital',
      'Painel administrativo',
      'Integração Discord e VIP',
      'Nexus Client (launcher)',
    ],
  },
];

const services = [
  {
    number: '01',
    title: 'Sistemas sob medida',
    text: 'Painéis, plataformas e automações desenhados ao redor da operação do seu negócio.',
  },
  {
    number: '02',
    title: 'Websites',
    text: 'Sites institucionais e experiências digitais rápidas, responsivas e memoráveis.',
  },
  {
    number: '03',
    title: 'Bots para Discord',
    text: 'Bots exclusivos para comunidades, com fluxos, moderação e integrações personalizadas.',
  },
  {
    number: '04',
    title: 'Design',
    text: 'Identidades visuais e interfaces que transformam estratégia em uma linguagem consistente.',
  },
  {
    number: '05',
    title: 'Infraestrutura',
    text: 'Arquitetura e otimização para servidores preparados para crescer com estabilidade.',
  },
];

const process = [
  ['Descoberta', 'Entendemos o contexto, as necessidades e o resultado esperado.'],
  ['Direção', 'Definimos escopo, linguagem visual e a melhor solução para o projeto.'],
  ['Construção', 'Design e desenvolvimento avançam juntos, com validações claras.'],
  ['Entrega', 'Publicamos, documentamos e acompanhamos a entrada em operação.'],
];

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
};

function getTeamGridClass(count) {
  if (count <= 1) return 'is-single';
  if (count === 2) return 'is-duo';
  return 'is-multi';
}

export default function Studio() {
  const { openProjectRequest } = useProjectRequest();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [team, setTeam] = useState([]);
  const [teamLoading, setTeamLoading] = useState(true);
  const [teamError, setTeamError] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    let cancelled = false;

    async function loadTeam() {
      setTeamLoading(true);
      setTeamError('');
      try {
        const response = await studioService.listTeam();
        if (!cancelled) {
          setTeam(response?.data?.team || []);
        }
      } catch (err) {
        if (!cancelled) {
          setTeam([]);
          setTeamError(err.message || 'Não foi possível carregar a equipe.');
        }
      } finally {
        if (!cancelled) setTeamLoading(false);
      }
    }

    loadTeam();
    return () => {
      cancelled = true;
    };
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="studio-page">
      <header className={`studio-nav${scrolled ? ' is-scrolled' : ''}`}>
        <div className="studio-shell studio-nav-inner">
          <Link to="/" className="studio-logo" onClick={closeMenu}>
            <VihsMark />
            Vih&apos;s <span>Studio</span>
          </Link>

          <nav className="studio-nav-links" aria-label="Navegação do Studio">
            <a href="#projetos">Projetos</a>
            <a href="#servicos">Serviços</a>
            <a href="#equipe">Equipe</a>
            <a href="#processo">Processo</a>
          </nav>

          <button
            type="button"
            className="studio-nav-cta"
            onClick={() => {
              closeMenu();
              openProjectRequest();
            }}
          >
            Iniciar um projeto
            <FaArrowRight aria-hidden="true" />
          </button>

          <button
            type="button"
            className="studio-menu-button"
            aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((current) => !current)}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        <div className={`studio-mobile-menu${menuOpen ? ' is-open' : ''}`}>
          <nav aria-label="Navegação móvel">
            <a href="#projetos" onClick={closeMenu}>Projetos</a>
            <a href="#servicos" onClick={closeMenu}>Serviços</a>
            <a href="#equipe" onClick={closeMenu}>Equipe</a>
            <a href="#processo" onClick={closeMenu}>Processo</a>
            <button
              type="button"
              onClick={() => {
                closeMenu();
                openProjectRequest();
              }}
            >
              Iniciar um projeto <FaArrowRight />
            </button>
          </nav>
        </div>
      </header>

      <main>
        <section className="studio-hero">
          <div className="studio-hero-atmosphere" aria-hidden="true">
            <span className="studio-hero-grid" />
            <span className="studio-hero-glow" />
            <span className="studio-hero-orbit" />
          </div>

          <div className="studio-shell studio-hero-inner">
            <motion.div
              className="studio-hero-copy"
              initial={{ opacity: 0, y: 34 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="studio-kicker">
                <span />
                Estúdio digital independente
              </p>
              <h1>
                Ideias com forma.
                <br />
                Produtos com <em>propósito.</em>
              </h1>
              <p className="studio-hero-lead">
                Criamos experiências digitais, sistemas e identidades que unem estratégia,
                tecnologia e atenção aos detalhes.
              </p>
              <div className="studio-hero-actions">
                <a href="#projetos" className="studio-button studio-button-primary">
                  Explorar projetos
                  <FaArrowRight aria-hidden="true" />
                </a>
                <button
                  type="button"
                  className="studio-button studio-button-text"
                  onClick={openProjectRequest}
                >
                  Solicitar projeto
                </button>
              </div>
            </motion.div>

            <motion.div
              className="studio-hero-art"
              initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1.1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              aria-hidden="true"
            >
              <div className="studio-art-card studio-art-card-back">
                <span>Digital</span>
                <strong>Experiences</strong>
              </div>
              <div className="studio-art-card studio-art-card-front">
                <div className="studio-art-top">
                  <span>VIH—S/26</span>
                  <span>BR</span>
                </div>
                <div className="studio-art-symbol">
                  <i />
                  <i />
                  <i />
                </div>
                <div className="studio-art-bottom">
                  <strong>Studio</strong>
                  <span>Estratégia · Design · Código</span>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="studio-shell studio-hero-foot">
            <span>Scroll para descobrir</span>
            <span>© {new Date().getFullYear()} — Brasil</span>
          </div>
        </section>

        <section id="projetos" className="studio-section studio-projects">
          <div className="studio-shell">
            <motion.header className="studio-section-header" {...reveal}>
              <div>
                <p className="studio-eyebrow">Cases / Clientes</p>
                <h2>Projetos entregues.</h2>
              </div>
              <p>
                Trabalhos realizados para clientes — da concepção à operação.
                A vitrine cresce conforme novas entregas entram no portfólio.
              </p>
            </motion.header>

            <div className="studio-project-list">
              {projects.map((project, index) => (
                <motion.article
                  className="studio-project"
                  key={project.name}
                  initial={{ opacity: 0, y: 34 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.7, delay: index * 0.08 }}
                >
                  <div className={`studio-project-visual is-${project.theme}`}>
                    <div className="studio-project-browser">
                      <div className="studio-browser-bar">
                        <span /><span /><span />
                        <i>{project.domain}</i>
                      </div>
                      <div
                        className="studio-browser-canvas has-image"
                        style={{ backgroundImage: `url(${project.image})` }}
                      >
                        <div className="studio-browser-overlay">
                          <img src={project.logo} alt="" className="studio-case-logo" />
                          <strong>{project.name}</strong>
                          <span className="studio-mock-label">{project.category}</span>
                        </div>
                      </div>
                    </div>
                    <span className="studio-project-watermark">{project.index}</span>
                    <span className="studio-project-badge">{project.label}</span>
                  </div>

                  <div className="studio-project-info">
                    <div className="studio-project-heading">
                      <span>{project.index}</span>
                      <div>
                        <p>{project.category}</p>
                        <h3>{project.name}</h3>
                      </div>
                    </div>
                    <p className="studio-project-client">Cliente · {project.client}</p>
                    <p className="studio-project-description">{project.description}</p>
                    <ul className="studio-project-deliverables">
                      {project.deliverables.map((item) => (
                        <li key={item}>
                          <FaCheck aria-hidden="true" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <ul className="studio-project-tags">
                      {project.tags.map((tag) => <li key={tag}>{tag}</li>)}
                    </ul>
                    {project.href && (
                      <a
                        href={project.href}
                        className="studio-project-link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver projeto ao vivo
                        <FaExternalLinkAlt aria-hidden="true" />
                      </a>
                    )}
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        <section id="servicos" className="studio-section studio-services">
          <div className="studio-shell">
            <motion.header className="studio-section-header" {...reveal}>
              <div>
                <p className="studio-eyebrow">O que fazemos</p>
                <h2>Da ideia à operação.</h2>
              </div>
              <p>
                Um parceiro para pensar, desenhar e construir soluções digitais completas,
                sem separar estética de funcionalidade.
              </p>
            </motion.header>

            <div className="studio-services-grid">
              {services.map((service, index) => (
                <motion.article
                  key={service.title}
                  className="studio-service-card"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.55, delay: index * 0.06 }}
                >
                  <span>{service.number}</span>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                  <FaArrowRight aria-hidden="true" />
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        <section id="equipe" className="studio-section studio-team">
          <div className="studio-shell">
            <motion.header className="studio-section-header" {...reveal}>
              <div>
                <p className="studio-eyebrow">Equipe</p>
                <h2>Quem constrói.</h2>
              </div>
              <p>
                Pessoas por trás de cada entrega — com visão clara, execução cuidadosa
                e compromisso com o resultado.
              </p>
            </motion.header>

            {teamLoading ? (
              <p className="studio-team-status">Carregando equipe...</p>
            ) : teamError ? (
              <p className="studio-team-status is-error">{teamError}</p>
            ) : team.length === 0 ? (
              <p className="studio-team-status">A equipe será apresentada em breve.</p>
            ) : (
              <div className={`studio-team-grid ${getTeamGridClass(team.length)}`}>
                {team.map((member, index) => {
                  const avatarSrc = member.avatarUrl || avatarVihs;
                  const discordHref = member.discordUrl || CONTACT_URL;
                  const instagramHref = member.instagramUrl || INSTAGRAM_URL;
                  const indexLabel = String(index + 1).padStart(2, '0');

                  return (
                    <motion.article
                      key={member.id || member.name}
                      className="studio-team-card"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-60px' }}
                      transition={{ duration: 0.55, delay: index * 0.06 }}
                    >
                      <div className="studio-team-avatar">
                        <img src={avatarSrc} alt={member.name} />
                      </div>
                      <div className="studio-team-body">
                        <div className="studio-team-meta">
                          <span className="studio-team-index" aria-hidden="true">{indexLabel}</span>
                          <p className="studio-team-role">{member.role}</p>
                        </div>
                        <h3>{member.name}</h3>
                        {member.focus ? <p className="studio-team-focus">{member.focus}</p> : null}
                        {member.bio ? <p className="studio-team-bio">{member.bio}</p> : null}
                        <div className="studio-team-links">
                          <a href={discordHref} target="_blank" rel="noopener noreferrer">
                            Discord
                          </a>
                          <span aria-hidden="true">·</span>
                          <a href={instagramHref} target="_blank" rel="noopener noreferrer">
                            Instagram
                          </a>
                        </div>
                      </div>
                    </motion.article>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section id="processo" className="studio-section studio-process">
          <div className="studio-shell studio-process-layout">
            <motion.div className="studio-process-intro" {...reveal}>
              <p className="studio-eyebrow">Nosso processo</p>
              <h2>Clareza em cada etapa.</h2>
              <p>
                Projetos bem resolvidos começam com boas perguntas. Nosso processo mantém
                estratégia, criação e execução na mesma direção.
              </p>
              <div className="studio-process-note">
                <FaCheck aria-hidden="true" />
                Escopo e comunicação transparentes
              </div>
            </motion.div>

            <ol className="studio-process-list">
              {process.map(([title, text], index) => (
                <motion.li
                  key={title}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.55, delay: index * 0.07 }}
                >
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>

        <section className="studio-contact">
          <div className="studio-contact-grid" aria-hidden="true" />
          <motion.div className="studio-shell studio-contact-inner" {...reveal}>
            <p className="studio-eyebrow">Seu projeto pode ser o próximo</p>
            <h2>Vamos construir algo que mereça ser lembrado.</h2>
            <a
              href={CONTACT_URL}
              className="studio-contact-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span><FaDiscord aria-hidden="true" /></span>
              Conversar no Discord
              <FaArrowRight aria-hidden="true" />
            </a>
          </motion.div>
        </section>
      </main>

      <footer className="studio-footer">
        <div className="studio-shell studio-footer-main">
          <div>
            <Link to="/" className="studio-footer-logo">
              <VihsMark />
              Vih&apos;s <span>Studio</span>
            </Link>
            <p>Criação digital sob medida.</p>
          </div>
          <div className="studio-footer-links">
            <div>
              <span>Navegação</span>
              <a href="#projetos">Projetos</a>
              <a href="#servicos">Serviços</a>
              <a href="#equipe">Equipe</a>
              <a href="#processo">Processo</a>
            </div>
            <div>
              <span>Contato</span>
              <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">Discord</a>
              <a href="https://instagram.com/vihsoficial" target="_blank" rel="noopener noreferrer">
                Instagram
              </a>
            </div>
          </div>
        </div>
        <div className="studio-shell studio-footer-bottom">
          <span>© {new Date().getFullYear()} Vih&apos;s. Todos os direitos reservados.</span>
          <div className="studio-footer-bottom-links">
            <Link to="/termos">Termos e Políticas</Link>
            <Link to="/">Voltar para Vih&apos;s</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
